import { useEffect, useMemo, useRef, useState } from 'react';

import { PURCHASE_STEP, VALIDITY_CATEGORY_URL_PARAM } from '~constants';
import {
  ConsumerCategory,
  Product,
  ProductCategory,
  ProductCategoryValidity,
} from '~types/appsync.types';
import { normalizeUrl, updateUrlParam } from '~utils';

import useProductCodeControl from '../useProductCodeControl/useProductCodeControl';
import useSlugProductMode from '../useSlugProduct/useSlugProduct';

export interface UseValidityCategorySelectorOptions {
  productCategory?: ProductCategory;
  products?: Product[];
  participantsLimit?: number;
  syncUrl?: boolean;
  /** Pre-fill validity category shortname from HTML attribute. Used as fallback when no URL param is present. */
  initialValidityCategoryShortname?: string;
}

export interface UseValidityCategorySelectorResult {
  validityCategory: ProductCategoryValidity | undefined;
  selectedProduct: Product | undefined;
  productLocked: boolean;
  isOpenProductCodeModal: boolean;
  invalidProductCode: boolean;
  configurationStep: PURCHASE_STEP;
  sortedConsumerCategories: ConsumerCategory[];
  sortedValidityCategories: ProductCategoryValidity[];
  handleValidityCategoryChange: (newValidityCategory?: ProductCategoryValidity) => void;
  handleProductCodeSubmit: (code: string | undefined) => void;
  handleCancelCodeModal: () => void;
  handleClickAddOptions: () => void;
  handleClickBackButton: () => void;
  setConfigurationStep: (step: PURCHASE_STEP) => void;
}

const useValidityCategorySelector = ({
  productCategory,
  products,
  participantsLimit,
  syncUrl = true,
  initialValidityCategoryShortname,
}: UseValidityCategorySelectorOptions): UseValidityCategorySelectorResult => {
  const [validityCategory, setValidityCategory] = useState<ProductCategoryValidity>();
  const [productLocked, setProductLocked] = useState(true);
  const [isOpenProductCodeModal, setIsOpenProductCodeModal] = useState(false);
  const [invalidProductCode, setInvalidProductCode] = useState(false);
  const [configurationStep, setConfigurationStep] = useState(PURCHASE_STEP.PRODUCT);
  const [productCodeMemoizer, setProductCodeMemoizer] = useState<string[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);

  const { isSlugProductMode } = useSlugProductMode();
  const [executeProductCodeControl] = useProductCodeControl();

  const sortedValidityCategories = useMemo(
    () =>
      [...(productCategory?.validities ?? [])]
        .filter((vc) => !!vc.name)
        .sort((a, b) => a.position - b.position),
    [productCategory?.validities],
  );

  // Auto-select validity category in slug-product mode when only one validity is available
  useEffect(() => {
    const singleValidity = sortedValidityCategories[0];
    const shouldAutoSelect =
      isSlugProductMode &&
      sortedValidityCategories.length === 1 &&
      singleValidity &&
      validityCategory?.id !== singleValidity.id;

    if (shouldAutoSelect) {
      setValidityCategory(singleValidity);
    }
  }, [isSlugProductMode, sortedValidityCategories, validityCategory?.id]);

  // Sync selectedProduct with validityCategory — only update when the product logically changes
  useEffect(() => {
    if (!products || !validityCategory) {
      setSelectedProduct(undefined);
      return;
    }
    const foundProduct = products.find(
      (product) => product.validityCategory?.shortname === validityCategory.shortname,
    );
    if (!foundProduct) {
      setSelectedProduct(undefined);
      return;
    }
    setSelectedProduct((prev) => {
      if (prev?.slug === foundProduct.slug) return prev;
      return {
        ...foundProduct,
        consumerCategories: [...foundProduct.consumerCategories].sort(
          (a, b) => a.position - b.position,
        ),
      };
    });
  }, [products, validityCategory]);

  // Normalize URL and handle product locking
  useEffect(() => {
    if (!selectedProduct) return;
    if (syncUrl) {
      const searchParams = new URL(globalThis.location.href).searchParams;
      normalizeUrl(searchParams, selectedProduct?.consumerCategories, participantsLimit);
    }
    setProductLocked(selectedProduct.protectedByCode);

    if (selectedProduct.protectedByCode) {
      setIsOpenProductCodeModal(!productCodeMemoizer.includes(selectedProduct.slug));
      setProductLocked(!productCodeMemoizer.includes(selectedProduct.slug));
      setInvalidProductCode(false);
    }
  }, [selectedProduct, productCodeMemoizer, participantsLimit, syncUrl]);

  // Init validity from URL or initial attribute — only when the logical category changes
  const prevCategoryIdRef = useRef<number>();
  useEffect(() => {
    if (!productCategory) return;
    if (prevCategoryIdRef.current === productCategory.id) return;
    prevCategoryIdRef.current = productCategory.id;

    const urlShort = syncUrl
      ? new URL(globalThis.location.href).searchParams.get(VALIDITY_CATEGORY_URL_PARAM)
      : undefined;
    // HTML attribute takes priority over URL param
    const short = initialValidityCategoryShortname ?? urlShort;

    if (short && productCategory.validities) {
      const found = productCategory.validities.find((v) => v.shortname === short);
      if (found) setValidityCategory(found);
    }
  }, [productCategory, syncUrl, initialValidityCategoryShortname]);

  const sortedConsumerCategories = useMemo(
    () => selectedProduct?.consumerCategories ?? [],
    [selectedProduct?.consumerCategories],
  );

  const handleValidityCategoryChange = (newValidityCategory?: ProductCategoryValidity) => {
    setValidityCategory(newValidityCategory);
    if (syncUrl) {
      updateUrlParam(VALIDITY_CATEGORY_URL_PARAM, newValidityCategory?.shortname);
    }
    setIsOpenProductCodeModal(false);
    setInvalidProductCode(false);
    setProductLocked(true);
  };

  const handleProductCodeSubmit = (code: string | undefined) => {
    if (selectedProduct?.slug && code) {
      executeProductCodeControl({
        variables: {
          codeToControl: code,
          productSlug: selectedProduct.slug,
        },
      }).then((response) => {
        if (response?.data?.controlCode?.status === 'OK') {
          setIsOpenProductCodeModal(false);
          setProductLocked(false);
          setProductCodeMemoizer((prev) => [...prev, selectedProduct.slug]);
        } else {
          setInvalidProductCode(true);
        }
      });
    }
  };

  const handleCancelCodeModal = () => {
    handleValidityCategoryChange();
  };

  const handleClickAddOptions = () => {
    setConfigurationStep(PURCHASE_STEP.OPTIONS);
  };

  const handleClickBackButton = () => {
    setConfigurationStep(PURCHASE_STEP.PRODUCT);
  };

  return {
    configurationStep,
    handleCancelCodeModal,
    handleClickAddOptions,
    handleClickBackButton,
    handleProductCodeSubmit,
    handleValidityCategoryChange,
    invalidProductCode,
    isOpenProductCodeModal,
    productLocked,
    selectedProduct,
    setConfigurationStep,
    sortedConsumerCategories,
    sortedValidityCategories,
    validityCategory,
  };
};

export default useValidityCategorySelector;
